import { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  //   applyAuthRules,
  AuthContext,
  authRules,
  getRedirectUrl,
  useRequireAuth,
} from '@zg-rentals/particles-js-auth-hooks';
import UserActions from '@common/actions/UserActions';
import AppConstants from '@common/constants/AppConstants';
import userPersonas from '@common/constants/userPersonas';
import { signInPageLandlord } from '@rental-manager/pathsManifest';
// import { hideRegLoginLightbox, showRegLoginLightbox } from '@zillow/user-session-store';

type Props = {
  requireStrongAuthentication: boolean;
  appType: string;
  brand: 'zillow' | 'hotpads' | 'trulia';
  router: {
    location: {
      pathname: string;
      search: string;
    };
  };
  setServerRedirectUrl?: (redirectUrl: any) => void;
  dispatch: (...args: Array<any>) => any;
  children: any;
};

const AuthCheckWrapper = (props: Props): JSX.Element => {
  const {
    requireStrongAuthentication = false,
    appType,
    brand,
    router,
    children,
    dispatch,
    setServerRedirectUrl,
  } = props;

  const auth = useContext(AuthContext);
  const [BlockedComponent, setBlockedComponent] = useState<JSX.Element>();
  const [lastAuth, setLastAuth] = useState(0);

  if (typeof window === 'undefined') {
    let to: string;
    if (appType === userPersonas.LANDLORD) {
      // Route landlord redirects through the Zillow-login SDK so they go through
      // the same entry point as the rest of rpw's landlord login flow.
      to = signInPageLandlord({
        pathname: router.location.pathname,
        search: router.location.search,
      });
    } else {
      const rules = authRules(auth, requireStrongAuthentication);
      to = getRedirectUrl({
        rules,
        brand,
        isInWebView: false,
        currentPathname: router.location.pathname,
        queryString: router.location.search,
      });
    }

    if (setServerRedirectUrl) dispatch(setServerRedirectUrl(to));
  }

  // asynchronously fetch and update user data in AuthContext and redux store
  // to extend a user's strong auth session after 30 seconds from last request
  useEffect(() => {
    let isComponentMounted = true;
    if (requireStrongAuthentication && Date.now() > lastAuth + 30000) {
      (async () => {
        const getUserRequest = await UserActions.getUser({
          appName: appType === userPersonas.LANDLORD ? AppConstants.RENTAL_MANAGER : AppConstants.RENTER_HUB,
          pageWithSensitiveInformation: requireStrongAuthentication,
          includeSummary: true,
          brand,
        });
        try {
          if (isComponentMounted) {
            setLastAuth(Date.now());
            const result = await dispatch(getUserRequest);
            auth.refreshSessionExpirationDate(result.sessionSecondsRemaining * 1000);
          }
          // eslint-disable-next-line no-empty
        } catch {}
      })();
    }

    return () => {
      isComponentMounted = false;
    };
  });

  // this hook will run on componentDidMount, and when
  // ... props change
  useRequireAuth({
    onBlocked: setBlockedComponent,
    requireStrongAuth: requireStrongAuthentication,
    currentPathname: router.location.pathname,
    queryString: router.location.search,
    brand,
    isInWebView: false,
  });

  // don't render any content if user is not logged in
  if (!auth.user.loggedIn) return <></>;
  else if (auth.user.isUserBlocked) return <>{BlockedComponent}</>;
  return <>{children}</>;
};

const mapStateToProps = (state) => ({
  brand: state.app.brand,
  router: state.router,
});

export default connect(mapStateToProps)(AuthCheckWrapper);
